import { useState } from 'react';
import type {
  IUserSettingsPrpPredefinedField,
  IUserSettingsPrpUdf,
  SubmittalDragType,
} from 'app/services/userSettingsApi';
import { deleteButtonWantsTop } from '../submittal/submittalModel';
import type {
  ProposalCmsDocument,
  ProposalDragPayload,
  ProposalLibraryType,
} from './proposalModel';

/**
 * Column 2 of the Proposal tab — `proposal.tmpl:43-172`. The **library**: the
 * three sources you can drop into the format rail, their chips, the search box
 * and the "Total Fields" counters.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * ★★ THE LIBRARY IS THE SAME IDEA AS SUBMITTAL'S AND ALMOST NOTHING IN IT IS
 * THE SAME MARKUP. Every difference below is transcribed, not tidied:
 *
 *   · **SIX user rows, SIX CMS, THREE application** — vs submittal's 283 / 6 / 20.
 *   · **No PDF anywhere.** The user rows have no `udf_type_value`, so there is
 *     no `icon-pdf` / `icon-text-format-black-24dp` fork and no type icon column
 *     at all: the proposal `<li>` is `<div class="row"><div class="col-xs-12">`
 *     with the anchor as a direct child, where submittal wraps it in a
 *     `.user-tab-row` + `.col-xs-1` + `.col-xs-11` grid.
 *   · the anchors carry `item-for-drag` and `draggable-item`, NOT
 *     `submittal-item-for-drag` / `submittal-draggable-item` — and
 *     `item-for-drag` is precisely the class the proposal sortable declares as
 *     its `items` (`proposal.js:616`), which is the whole
 *     `connectToSortable` trick.
 *   · the user `<li>` carries BOTH `li-{{id}}` (which
 *     `removeUserDefinedField` removes by class) and `data-id="{{id}}"` (which
 *     the same function's sibling lookup queries by attribute) — submittal uses
 *     `defined-li-{{id}}` for the first job and nothing for the second.
 *   · the user anchor carries `data-target` / `data-model` / `data-cms`, all of
 *     which interpolate to EMPTY (a `PRPUserDefinedField` has no such fields).
 *     Kept: they are in the rendered DOM and `clickToItem` reads `data-cms` off
 *     the anchor.
 *   · the CMS rows have no `.p-0` / `.row.ui-state-default` nesting difference
 *     worth listing — but they DO differ: proposal's `<a>` sits inside
 *     `.row.d-flex` with a `.col-xs-1` icon, and its trailing newline before
 *     `</a>` is preserved as a trailing space.
 *   · the App rows use `icon-pdf` with an inline `style` on the icon column, and
 *     their `<li data-id="app-{{value}}">` matches submittal's convention.
 *   · `#dragging-element` — an unused, permanently `hide`den div
 *     (`proposal.tmpl:160`) that exists on this tab and not on submittal.
 * ═════════════════════════════════════════════════════════════════════════════
 *
 * ★ `id="myTab"` APPEARS THREE TIMES HERE, on top of submittal's three — part of
 * Q3's duplicate-id family (six `#myTab` on the real page). Kept.
 *
 * ★ Q6 — the counters read the SNAPSHOT length and are never touched by the
 * search. Do not "fix" it by counting visible rows.
 *
 * ★ THE SEARCH FILTERS ON THE SNAPSHOT'S NAME. Legacy filters on the `<li>`'s
 * `data-name` ATTRIBUTE (`search`, `:712-723`). On this tab a rename triggers a
 * full `render()` (see `ProposalTab`'s banner), so the attribute is rewritten
 * almost immediately — unlike submittal, where it goes stale. Feeding it from
 * the render snapshot reproduces both.
 *
 * ★★ THE THREE `*_tab` HANDLERS ARE DEAD ON THIS TAB — see `ProposalTab`'s
 * `#proposal` note. They are meant to strip `.active` from the other two `<ul>`s
 * so the three lists behave as one exclusive group; their selectors are rooted
 * at `#proposal`, which resolves to the NAV ANCHOR (the proposal pane's own root
 * is `id="submittal"` — Q3), so they match nothing. `activeItemKeys` is
 * therefore a set of PER-LIST keys and exclusivity is enforced per `<ul>`, not
 * across all three.
 */

interface ProposalFieldLibraryProps {
  /** The render snapshot's user rows — NOT the live collection. */
  userFields: IUserSettingsPrpUdf[];
  cmsDocuments: ProposalCmsDocument[];
  appFields: IUserSettingsPrpPredefinedField[];
  /** `self.activeDefinedsField`. ★ Can legitimately be `undefined`: clicking the
   * `+` button while the User chip is `.not-active` bubbles to the chip handler
   * with a target that has no `data-type`, and legacy assigns that `undefined`
   * straight onto `self.activeDefinedsField` — blanking every chip, every
   * `.show-row` and every counter until another chip is clicked. Reproduced. */
  activeType: ProposalLibraryType | undefined;
  counts: { user: number; cms: number; application: number };
  /** `search()` (`:712-723`) — lower-cased substring over `data-name`. */
  searchValue: string;
  /** `changeActiveDefinedsField` `.hide`s BOTH the wrapper div and the input
   * (they share `proposal-search-field`). ★ A full `render()` does NOT restore
   * it — the template emits it unhidden — so the flag is reset by `doRender`. */
  searchHidden: boolean;
  onSearchChange: (value: string) => void;
  /** `click .block-fields-wrap.not-active` → `changeActiveDefinedsField`
   * (`:378-405`). Note the bound selector is the BARE `.block-fields-wrap`,
   * where submittal binds `.submittal-block-fields-wrap`. */
  onChipClick: (type: ProposalLibraryType | undefined) => void;
  /** `click #add_user_defined_field` → `addUserDefinedField` (`:217-238`).
   * ★ NO POPUP — the Proposal tab creates immediately. */
  onAddClick: () => void;
  /** `click .each-element-row` → `clickToItem`, plus Bootstrap's
   * `data-toggle="tab"` activation of the anchor. */
  onItemClick: (type: ProposalLibraryType, item: ProposalLibraryItemKey) => void;
  /** `click .remove` → `render_popup` (`:437-457`). ★ Bound to the BARE
   * `.remove`, so it matches the `<i>` as well as the `<button>` — but
   * `render_popup` calls `stopPropagation`, so an icon click still opens exactly
   * ONE popup. */
  onRemoveClick: (fieldId: number) => void;
  /** ★★ Q23 — `click .btn-udf-delete` → `activateTab` (`:360-377`). The trash
   * BUTTON carries both `.remove` and `.btn-udf-delete`, and `stopPropagation`
   * inside `render_popup` does not stop a sibling handler on the SAME element —
   * so pressing the button's own padding opens the delete popup AND activates
   * that row. Pressing the nested `<i>` does not (the bubble is stopped before
   * the button's handlers run). Two separate props for exactly that reason. */
  onDeleteButtonActivate: (fieldId: number) => void;
  /** Which `<li>`s carry `.active`. A SET because `active_last_change`
   * (`:295-300`) uses `addClass` with no matching remove, and because the three
   * `*_tab` handlers that would enforce cross-list exclusivity are dead here. */
  activeItemKeys: ReadonlySet<string>;
  /** `showTooltip` (`:659-664`) walks THREE `parentNode`s up from an overflowing
   * `a.user-defiened-fields` (submittal walks five) — landing on the `<li>`. */
  tooltipItemKeys: ReadonlySet<string>;
  /** Registered so `clickToItem`'s `scrollIntoView` can find its row. */
  itemRefs: React.MutableRefObject<Map<string, HTMLLIElement>>;
  anchorRefs: React.MutableRefObject<Map<string, HTMLAnchorElement>>;
  /** Handed up so `render()` can restore `.proposal-user-udf-scroll`'s
   * `scrollTop` (`:147,180`) — a submittal-free behaviour. */
  userScrollRef: React.MutableRefObject<HTMLDivElement | null>;
  /** The press that arms `draggable({connectToSortable})` (`:638-654`). Hands
   * `useLibraryDrag` the four attributes the drop will read — legacy's
   * `self.dropping = true` payload. The clone helper, the `distance: 1`
   * threshold, `revert: true` and the `stop` handler all live in that hook. */
  onItemMouseDown: (e: React.MouseEvent<HTMLAnchorElement>, payload: ProposalDragPayload) => void;
}

/** Identifies one library row across the three heterogeneous sources. */
export interface ProposalLibraryItemKey {
  /** `user:<id>` / `cms:<value>` / `application:<value>`. */
  key: string;
  /** The `<li>`'s `data-id`: user → `<id>`, cms → `<value>`, app → `app-<value>`. */
  liId: string;
  /** The `<a>`'s `data-id` — user/app only; CMS anchors have none. */
  anchorId?: number;
  /** The `<a>`'s `data-cms` — CMS only. */
  cms?: string;
  /** The `tab-pane` this row activates. ★ Built from `field_order` for user
   * rows, from the bare `value` for app rows and from `cms_terms_<value>` for
   * CMS ones — none of which carries submittal's `submittal_` prefix. */
  paneId: string;
}

export const proposalItemKey = (type: ProposalLibraryType, id: string | number) => `${type}:${id}`;

const ProposalFieldLibrary = ({
  userFields,
  cmsDocuments,
  appFields,
  activeType,
  counts,
  searchValue,
  searchHidden,
  onSearchChange,
  onChipClick,
  onAddClick,
  onItemClick,
  onRemoveClick,
  onDeleteButtonActivate,
  activeItemKeys,
  tooltipItemKeys,
  itemRefs,
  anchorRefs,
  userScrollRef,
  onItemMouseDown,
}: ProposalFieldLibraryProps) => {
  /**
   * The drag handlers, spread identically onto all three anchor kinds.
   *
   * The payload is read back OFF THE ELEMENT'S ATTRIBUTES rather than closed
   * over from the row data, because that is literally what `createNewField`
   * does (`proposal.js:562-565`, four `e.attr(...)` calls on the dropped helper)
   * and it is the reason the three drops send three different bodies: a missing
   * attribute is `undefined`, and `JSON.stringify` drops undefined keys, which
   * is how a CMS drop ends up with **no `object_id`** and a user drop with no
   * `cms_field_name`. Deriving the payload from props would quietly turn those
   * absences into `null`s and change the wire.
   */
  const anchorDragProps = {
    onMouseDown: (e: React.MouseEvent<HTMLAnchorElement>) => {
      const a = e.currentTarget;
      onItemMouseDown(e, {
        // `||`, not `??`: the user anchors have no `data-type` at all, and
        // legacy's fallback also catches an empty one.
        type: (a.getAttribute('data-type') as SubmittalDragType | null) || 'user_defined',
        name: a.getAttribute('data-name') ?? undefined,
        id: a.getAttribute('data-id') ?? undefined,
        text: a.getAttribute('data-text') ?? '',
      });
      e.stopPropagation();
    },
    /** The `draggable="true"` attribute is legacy markup and stays; the native
     * drag it would start does not. jQuery-UI stops it by `preventDefault`ing
     * the press, which is the same thing one beat earlier — see
     * `submittal/FieldLibrary.tsx`. */
    onDragStart: (e: React.DragEvent<HTMLAnchorElement>) => e.preventDefault(),
  };

  /** ★ Q20's `.top` flip on the per-row trash buttons. */
  const [topButtons, setTopButtons] = useState<ReadonlySet<number>>(() => new Set());
  const flipTop = (id: number, target: HTMLElement) => {
    const wants = deleteButtonWantsTop(target);
    setTopButtons((prev) => {
      if (wants === prev.has(id)) return prev;
      const next = new Set(prev);
      if (wants) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const chipClass = (type: ProposalLibraryType) =>
    `block-fields-wrap proposal-block-fields-wrap ${type !== activeType ? 'not-active' : ''}`;

  const rowClass = (base: string, type: ProposalLibraryType) =>
    `${base} ${type === activeType ? 'show-row' : ''}`;

  const query = searchValue.toLowerCase();

  const chipProps = (type: ProposalLibraryType) => ({
    className: chipClass(type),
    'data-type': type,
    onClick: (e: React.MouseEvent<HTMLDivElement>) =>
      activeType !== type &&
      onChipClick(
        ((e.target as HTMLElement).getAttribute('data-type') as ProposalLibraryType | null) ??
          undefined
      ),
  });

  return (
    <div className="col-xs-12 full-height col-md-6 col-lg-4 right-column-scrollable pt-0 pr-0">
      <div className="available_fields block-title">Defined Fields</div>
      <div className="col-xs-12 scrollable pr-0 overflow-x-hidden">
        <div className="col-xs-12">
          <div className="d-flex block-fields">
            {/* eslint-disable jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */}
            <div {...chipProps('user')}>
              <i className="icon-baseline-account-box-24px" data-type="user" />{' '}
              <span data-type="user">User</span>{' '}
              {/* ★ The `+` is rendered ONLY while the User chip is active
                  (`proposal.tmpl:51-53` wraps it in `{{#ifCompare "user" "=="
                  activeDefinedsField}}`), which is a real divergence from
                  submittal — where the button is always present and clicking it
                  from an inactive chip is what blanks `activeDefinedsField`.
                  Here it cannot be reached from an inactive chip at all. */}
              {activeType === 'user' && (
                <button id="add_user_defined_field" className="btn-add btn add" onClick={onAddClick}>
                  +
                </button>
              )}
            </div>
            <div {...chipProps('cms')}>
              <i className="icon-cms-fields cms" data-type="cms" /> <span data-type="cms">CMS</span>
            </div>
            <div {...chipProps('application')}>
              <i className="x-icon" data-type="application" />{' '}
              <span data-type="application">Application</span>
            </div>
            {/* eslint-enable jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */}
          </div>
        </div>
        {/* ★ The template hides the wrapper with `{{#ifCompare "user" "!="
            activeDefinedsField}}hide{{/ifCompare}}` on the FIRST render, and
            `changeActiveDefinedsField` (`:382-383`) then toggles `.hide` on
            `$('.proposal-search-field')` — which matches BOTH the wrapper and the
            input, because they share the class. Both carry it here. */}
        <div className={`col-xs-12 proposal-search-field${searchHidden ? ' hide' : ''}`}>
          <input
            type="search"
            className={`proposal-search-field search-field-input${searchHidden ? ' hide' : ''}`}
            placeholder="Search..."
            value={searchValue}
            onChange={(e) => onSearchChange(e.target.value)}
          />
        </div>
        <div className="col-xs-12 fields-container pr-0" id="proposal-row-container">
          {/* ── USER ─────────────────────────────────────────────────────── */}
          <div className={rowClass('row h-100 proposal-row user-row hide-row', 'user')} data-type="user">
            <div className="col-xs-12 h-100">
              <div
                className="udf-scroll user-udf-scroll ml--5 proposal-user-udf-scroll"
                ref={userScrollRef}
              >
                <ul className="nav nav-tabs user-fields-block row" id="myTab" role="tablist">
                  {userFields.map((field) => {
                    const key = proposalItemKey('user', field.id);
                    const name = field.name ?? '';
                    const hidden = !name.toLowerCase().includes(query);
                    const paneId = `proposal_user_defined_field_${field.field_order}`;
                    return (
                      /* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions */
                      <li
                        key={field.id}
                        className={[
                          'nav-item',
                          'user-tab',
                          'each-element-row',
                          'col-xs-12',
                          `li-${field.id}`,
                          activeItemKeys.has(key) ? 'active' : '',
                          tooltipItemKeys.has(key) ? 'with_tooltip_element' : '',
                          hidden ? 'hide' : '',
                        ]
                          .filter(Boolean)
                          .join(' ')}
                        /* ★ Built from `field_order`, not `id` — and so is the
                           pane. `active_last_change` and the create flow both
                           look rows up by that key. */
                        id={`proposal_udf_item_field_${field.field_order}`}
                        data-id={field.id}
                        data-name={name}
                        ref={(el) => {
                          if (el) itemRefs.current.set(key, el);
                          else itemRefs.current.delete(key);
                        }}
                        onClick={() =>
                          onItemClick('user', {
                            key,
                            liId: String(field.id),
                            anchorId: field.id,
                            paneId,
                          })
                        }
                      >
                        <div className="row">
                          <div className="col-xs-12">
                            <a
                              className="user-defiened-fields ui-state-default draggable-item ui-draggable ui-draggable-handle item-for-drag active"
                              id={`${paneId}-tab`}
                              data-toggle="tab"
                              href={`#${paneId}`}
                              role="tab"
                              aria-controls={`${paneId}-tab`}
                              aria-selected="true"
                              data-id={field.id}
                              draggable="true"
                              data-text={name}
                              /* ★ These three interpolate to EMPTY on a PRP UDF —
                                 the model has no such fields. Rendered because
                                 they are in the legacy DOM and `clickToItem`
                                 reads `data-cms` off the anchor. */
                              data-target=""
                              data-model=""
                              data-cms=""
                              ref={(el) => {
                                if (el) anchorRefs.current.set(key, el);
                                else anchorRefs.current.delete(key);
                              }}
                              onClick={(e) => e.preventDefault()}
                              {...anchorDragProps}
                            >
                              {name}
                            </a>
                            <div className="dropdown  dropdown_user_fields">
                              <div className="">
                                {/* ★★ Q23 — this ONE element carries `.remove`
                                    AND `.btn-udf-delete`, so a press on it runs
                                    `render_popup` (the delete confirmation) and
                                    `activateTab` (which activates the row and its
                                    pane) in that order. The nested `<i>` matches
                                    only `.remove`, and `render_popup`'s
                                    `stopPropagation` keeps the button's handlers
                                    from running for it — so the icon does NOT
                                    activate the tab. Both halves reproduced. */}
                                {/* eslint-disable-next-line jsx-a11y/mouse-events-have-key-events */}
                                <button
                                  data-parent=".user-udf-scroll"
                                  className={`ka-has-tooltip btn btn-udf-delete dropdown-toggle icon-more-actions remove delete-btn-content${
                                    topButtons.has(field.id) ? ' top' : ''
                                  }`}
                                  type="button"
                                  data-toggle="dropdown"
                                  data-id={field.id}
                                  tooltip="Delete"
                                  onMouseOver={(e) => flipTop(field.id, e.target as HTMLElement)}
                                  onClick={(e) => {
                                    // `render_popup` first (binding order,
                                    // `proposal.js:62` before `:69`), then
                                    // `activateTab` — both on this element.
                                    e.stopPropagation();
                                    onRemoveClick(field.id);
                                    onDeleteButtonActivate(field.id);
                                  }}
                                >
                                  {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/click-events-have-key-events */}
                                  <i
                                    data-id={field.id}
                                    className="icon-trash remove"
                                    onClick={(e) => {
                                      // `render_popup`'s own `stopPropagation`
                                      // (`:439`) — which is why hitting the icon
                                      // never reaches `activateTab`.
                                      e.stopPropagation();
                                      onRemoveClick(field.id);
                                    }}
                                  />
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>
          </div>

          {/* ── CMS ──────────────────────────────────────────────────────── */}
          <div className={rowClass('cms-fields proposal-row cms-row hide-row', 'cms')} data-type="cms">
            <div className="row h-100">
              <ul className="nav nav-tabs cms-fields-block" id="myTab" role="tablist">
                {cmsDocuments.map((doc) => {
                  const key = proposalItemKey('cms', doc.value);
                  return (
                    /* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions */
                    <li
                      key={doc.value}
                      className={`nav-item col-xs-12 each-element-row${
                        activeItemKeys.has(key) ? ' active' : ''
                      }`}
                      data-id={doc.value}
                      ref={(el) => {
                        if (el) itemRefs.current.set(key, el);
                        else itemRefs.current.delete(key);
                      }}
                      onClick={() =>
                        onItemClick('cms', { key, liId: doc.value, cms: doc.value, paneId: doc.key })
                      }
                    >
                      <div className="col-xs-12 p-0">
                        <div className="col-xs-12">
                          <div className="row ui-state-default">
                            <div className="col-xs-12 cms-tab">
                              <div className="row d-flex">
                                <div className="col-xs-1">
                                  <i className="icon-text-format-black-24dp udf-type-icon" />
                                </div>
                                <div className="col-xs-11 padding-left-0">
                                  <a
                                    draggable="true"
                                    className="cms-fields active cms-fields-a ui-state-default draggable-item ui-draggable ui-draggable-handle item-for-drag"
                                    id={`${doc.key}-tab`}
                                    data-toggle="tab"
                                    data-type="cms_terms"
                                    href={`#${doc.key}`}
                                    role="tab"
                                    aria-controls={doc.key}
                                    aria-selected="true"
                                    data-name={doc.value}
                                    data-cms={doc.value}
                                    data-text={doc.label}
                                    ref={(el) => {
                                      if (el) anchorRefs.current.set(key, el);
                                      else anchorRefs.current.delete(key);
                                    }}
                                    onClick={(e) => e.preventDefault()}
                                    {...anchorDragProps}
                                  >
                                    {/* The template breaks the line before
                                        `</a>`, so the anchor's text ends in
                                        whitespace. Kept. */}
                                    {doc.label}{' '}
                                  </a>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>

          {/* ── APPLICATION ──────────────────────────────────────────────── */}
          <div
            className={rowClass('app-fields proposal-row application-row hide-row', 'application')}
            data-type="application"
          >
            <div className="row h-100">
              <ul className="nav nav-tabs app-fields-block" id="myTab" role="tablist">
                {appFields.map((field) => {
                  const key = proposalItemKey('application', field.value);
                  return (
                    /* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions */
                    <li
                      key={field.id}
                      className={`nav-item col-xs-12 each-element-row${
                        activeItemKeys.has(key) ? ' active' : ''
                      }`}
                      data-id={`app-${field.value}`}
                      ref={(el) => {
                        if (el) itemRefs.current.set(key, el);
                        else itemRefs.current.delete(key);
                      }}
                      onClick={() =>
                        onItemClick('application', {
                          key,
                          liId: `app-${field.value}`,
                          anchorId: field.id,
                          /* ★ RAW `value` — the proposal app pane has no
                             prefix, so this tab owns `#equipment_list`,
                             `#price_summary` and `#equipment_description` at
                             document level. */
                          paneId: field.value,
                        })
                      }
                    >
                      <div className="col-xs-12 p-0">
                        <div className="col-xs-12">
                          <div className="row ui-state-default">
                            <div className="col-xs-12 app-tab">
                              <div
                                className="col-xs-1"
                                style={{ paddingBottom: '7px', paddingLeft: '5px' }}
                              >
                                <i className="icon-pdf udf-type-icon" />
                              </div>
                              <div className="col-xs-11 padding-left-0">
                                <a
                                  draggable="true"
                                  className="app-fields active cms-fields-a ui-state-default draggable-item ui-draggable ui-draggable-handle item-for-drag"
                                  data-type="app_defined"
                                  id={`${field.value}-tab`}
                                  data-toggle="tab"
                                  href={`#${field.value}`}
                                  role="tab"
                                  aria-controls={field.value}
                                  aria-selected="true"
                                  data-id={field.id}
                                  /* Empty on a master row, exactly as the
                                     template interpolates them. */
                                  data-target=""
                                  data-model=""
                                  data-cms=""
                                  data-text={field.name}
                                  ref={(el) => {
                                    if (el) anchorRefs.current.set(key, el);
                                    else anchorRefs.current.delete(key);
                                  }}
                                  onClick={(e) => e.preventDefault()}
                                  {...anchorDragProps}
                                >
                                  {field.name}
                                </a>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        </div>
        {/* `proposal.tmpl:160` — present, permanently hidden, referenced by
            nothing. Rendered for DOM parity. */}
        <div id="dragging-element" className="hide" />
      </div>
      <div>
        {/* `render()` un-hides exactly one of these (`:166-168`). ★ Q6: the
            numbers are the collection lengths and never react to the search. */}
        <div className={`fields-count ${activeType === 'user' ? '' : 'hide '}user proposal-fields-count`}>
          Total Fields : <span className="count">{counts.user}</span>
        </div>
        <div
          className={`fields-count ${
            activeType === 'application' ? '' : 'hide '
          }application proposal-fields-count`}
        >
          Total Fields : <span className="count">{counts.application}</span>
        </div>
        <div className={`fields-count ${activeType === 'cms' ? '' : 'hide '}cms proposal-fields-count`}>
          Total Fields : <span className="count">{counts.cms}</span>
        </div>
      </div>
    </div>
  );
};

export default ProposalFieldLibrary;
